"use client";

import { useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { OrgAvatar } from "@/features/active-org/_components/OrgAvatar";
import { searchEnterpriseOrganizations } from "@/features/domain-management/_logic/actions/org-search-actions";
import {
  SwitcherSearchPanel,
  type SwitcherOption,
  type SwitcherSearchLabels,
} from "@/features/event-ownership/_components/SwitcherSearchPanel";
import { useSwitcherList } from "@/features/event-ownership/_hooks/use-switcher-list";
import { MODAL_PAGE_SIZE } from "@/features/user-management/_logic/constants";
import type { EnterpriseOrganization } from "@/lib/enterprise/types";

import { ADMIN_ORG_ID_PARAM } from "../_logic/constants";

/** Module-scope so the object identity is stable across renders (mirrors `OrgChangePopover`'s own copy). */
const ORG_SEARCH_LABELS: SwitcherSearchLabels = {
  placeholder: "Find organization...",
  listLabel: "Organizations",
  noun: "organizations",
};

/**
 * Adapts {@link searchEnterpriseOrganizations} (KTD9: `enterprise_organizations.id` space, the space
 * `admin_org_id` requires, not legacy `orgs.org_id`) to the `{ success, data }` shape
 * `useSwitcherList`/`useSearchableModalList` expect. `listAction`/`searchAction` differ only in whether
 * a term is passed, so one function covers both.
 *
 * The page size is passed explicitly: `useSearchableModalList` infers "there is another page" from
 * `data.length >= MODAL_PAGE_SIZE`, so if the action paged at its own default instead, the two sizes
 * could drift and the list would silently stop loading early or loop on a stuck sentinel.
 *
 * @param term - The name search term, or `""` for the unfiltered list.
 * @param offset - Pagination offset.
 * @returns The page of enterprise orgs, with `success: false` when the lookup failed.
 */
async function fetchEnterpriseOrgs(
  term: string,
  offset = 0
): Promise<{ success: boolean; data?: EnterpriseOrganization[] }> {
  // withIcons: true -- this is the one caller of searchEnterpriseOrganizations that renders icon_url.
  const { orgs, error } = await searchEnterpriseOrganizations(term, offset, MODAL_PAGE_SIZE, true);
  return { success: !error, data: orgs };
}

/**
 * The org-picker content for `AdminEntryGate`'s `"org"` step: a `SwitcherSearchPanel` listing
 * enterprise organizations (KTD9), reusing the same browse/search/paging machinery as the
 * event-ownership "Change organization" popover rather than a bespoke list. Unlike that popover this
 * is never unmounted between opens, so it always loads its first page on mount (`isOpen: true`
 * passed to {@link useSwitcherList} is a constant, not a real open/close toggle).
 *
 * Picking an org navigates to `/create?admin_org_id=<enterprise_organizations.id>` (client-side, via
 * `router.push`), which re-runs `resolveAdminCreateEntryDecision` server-side and lands on the owner
 * step next, since `admin_owner_id` is still absent.
 *
 * @returns The org search panel.
 */
export function AdminOrgPickerStep(): React.ReactElement {
  // Hooks
  const router = useRouter();
  const searchParams = useSearchParams();
  const { search, loadError } = useSwitcherList<EnterpriseOrganization>({
    isOpen: true,
    listAction: (offset) => fetchEnterpriseOrgs("", offset),
    searchAction: (term) => fetchEnterpriseOrgs(term),
    // KTD16: an inline notice scoped to this panel (rendered by `SwitcherSearchPanel` as
    // `role="alert"`), not a toast. There's no "close and reopen" here (unlike the popover this
    // pattern was written for) since this is a full page, so the copy points at a reload instead.
    loadErrorMessage: "Couldn't load organizations. Refresh the page to try again.",
    searchErrorMessage: "Couldn't search organizations. Try again.",
  });

  // Handlers
  const toOption = useCallback(
    (org: EnterpriseOrganization): SwitcherOption => ({
      key: org.id,
      icon: <OrgAvatar name={org.name} iconUrl={org.icon_url} logoUrl={org.logo_url} size="sm" />,
      label: org.name,
    }),
    []
  );

  const handleSelect = useCallback(
    (org: EnterpriseOrganization): void => {
      // Preserve every other param this visit arrived with (Northstar's `northstar`/`ns_id`,
      // `fresh`, `resume`) so passing through this gate doesn't silently drop that context —
      // only admin_org_id itself is ours to set.
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(ADMIN_ORG_ID_PARAM, String(org.id));
      router.push(`/create?${nextParams.toString()}`);
    },
    [router, searchParams]
  );

  // Render
  return (
    <SwitcherSearchPanel
      search={search}
      toOption={toOption}
      onSelect={handleSelect}
      labels={ORG_SEARCH_LABELS}
      notice={loadError}
    />
  );
}
